import classNames from 'classnames';
import { Link } from 'gatsby';
import React, { useState } from 'react';

import { IterablePopup } from '../iterablePopup';
import { MarketoPopup } from '../marketo-popup';
import { RightArrow } from '../right-arrow';
import * as styles from './blog-simple-banner-cta.module.css';

type LinkType =
	| 'directoryLink'
	| 'externalLink'
	| 'internalLink'
	| 'iterablePopup'
	| 'marketoPopup';

interface BlogSimpleBannerCtaProps {
	ctaAudience: string;
	ctaLinkLabel: string;
	ctaStyle: string;
	iterablePopup?: string;
	landingPageRoute: string;
	link: string;
	linkType: LinkType;
	marketoPopup?: any;
	openInNewTab: boolean;
	route: string;
}

export const BlogSimpleBannerCta: React.FC<BlogSimpleBannerCtaProps> = ({
	ctaAudience,
	ctaLinkLabel,
	ctaStyle,
	iterablePopup,
	landingPageRoute,
	link,
	linkType,
	marketoPopup,
	openInNewTab,
	route,
}) => {
	const [iterablePopupOpen, setIterablePopupOpen] = useState(false);
	const [marketoPopupOpen, setMarketoPopupOpen] = useState(false);

	const isDirectoryLink = linkType === 'directoryLink';
	const isExternalLink = linkType === 'externalLink';
	const isInternalLink = linkType === 'internalLink';
	const isIterablePopup = linkType === 'iterablePopup';
	const isMarketoPopup = linkType === 'marketoPopup';
	const isProviderCta = ctaAudience === 'provider';

	const handleAnalytics = () => {
		if (isIterablePopup || isDirectoryLink) {
			const eventName = isIterablePopup
				? 'article_Iterable_CTA_btn'
				: 'blog_directory_CTA_btn';

			if (window?.gtag) {
				window.gtag('event', eventName, {
					send_to: 'G-R0Z6J0E1GE',
					cta_name: ctaLinkLabel,
					cta_url: link || route || landingPageRoute,
				});
			}
		}
	};

	const commonContent = (
		<div
			className={classNames(
				styles.contentContainer,
				isProviderCta ? styles.providerCta : styles.consumerCta,
				styles[`${ctaStyle}Background`]
			)}
			onClick={handleAnalytics}
		>
			<h2 className={styles.heading}>{ctaLinkLabel}</h2>
			<RightArrow color='white' position='right' />
		</div>
	);

	const handleIterablePopup = () => {
		setIterablePopupOpen(!iterablePopupOpen);
	};

	const handleMarketoPopup = () => {
		setMarketoPopupOpen(!marketoPopupOpen);
	};

	const renderComponent = () => {
		if (isIterablePopup) {
			return (
				<button
					aria-label={ctaLinkLabel}
					className={styles.wrapper}
					onClick={handleIterablePopup}
				>
					{commonContent}
					<IterablePopup
						iterableForm={iterablePopup}
						iterablePopupOpen={iterablePopupOpen}
						setIterablePopupOpen={setIterablePopupOpen}
					/>
				</button>
			);
		}

		if (isInternalLink && (route || landingPageRoute)) {
			return (
				<Link className={styles.wrapper} to={route || landingPageRoute}>
					{commonContent}
				</Link>
			);
		}

		if ((isDirectoryLink || isExternalLink) && link) {
			return (
				<a
					aria-label={ctaLinkLabel}
					className={styles.wrapper}
					href={link}
					target={openInNewTab ? '_blank' : undefined}
					rel={openInNewTab ? 'noopener noreferrer' : undefined}
				>
					{commonContent}
				</a>
			);
		}

		if (isMarketoPopup) {
			return (
				<button className={styles.wrapper} onClick={handleMarketoPopup}>
					{commonContent}
					<MarketoPopup
						marketoForm={marketoPopup}
						marketoPopupOpen={marketoPopupOpen}
						setMarketoPopupOpen={setMarketoPopupOpen}
					/>
				</button>
			);
		}
	};

	return <>{renderComponent()}</>;
};
